import { useState } from 'react';
import ToastSuccess from '../toast/ToastSuccess';
import ToastError from '../toast/ToastError';
import { convertNumberToPrice, patternTelInput, removePropertyEmptyRequirement } from '@helper/functions.js';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@services/axios.js';
import SideSheets from '@/core/SideSheets';
import SideSheetsBody from '@/core/SideSheets.Body';
import SideSheetsFooter from '@/core/SideSheets.Footer';
import Button from '@/core/Button';
import Num2persian from 'num2persian';
import { Select, SelectItem } from '@heroui/react';
import clsx from 'clsx';
import facilitiesData from '@/constants/AdCreateOptions/facilitiesData';
import { useDarkMode } from '@/context/DarkModeContext';

export default function AddCustomer({ setShowModalAdd }) {
  const [showMore, setShowMore] = useState(false);
  const [priority, setPriority] = useState('بدون وضعیت');
  const { isDark } = useDarkMode();

  const queryClient = useQueryClient();

  const priorityStyle = {
    بالقوه: {
      bg: '#DBFBEE',
      color: '#1BA781',
      darkBg: '#DBFBEE',
      darkColor: '#1BA781',
    },
    میانه: {
      bg: '#FFF5E1',
      color: '#FFA800',
      darkBg: '#FFF5E1',
      darkColor: '#FFA800',
    },
    عادی: {
      bg: '#FFEBEC',
      color: '#E11640',
      darkBg: '#FFEBEC',
      darkColor: '#E11640',
    },
    'بدون وضعیت': {
      bg: '#fff',
      color: '#61616199',
      darkBg: '#1F2937',
      darkColor: '#9CA3AF',
    },
  };

  const [showRequirement, setShowRequirement] = useState(false);

  const { isPending, mutate } = useMutation({
    mutationKey: ['customer-create'],
    mutationFn: async (form) => {
      await api.post('panel/customer', form);
    },
    enabled: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list-customers'] });
      ToastSuccess({ content: 'مشتری جدید با موفقیت اضافه شد !' });
      setShowModalAdd(false);
    },
    onError: () => {
      ToastError({ content: 'مشکلی از سمت سرور پیش آمده !' });
    },
  });

  const submitHandler = (v) => {
    const form = {
      fullName: v.fullName,
      type: v.type,
      phoneNumber: v.phoneNumber,
      note: v.note,
      priority,
      transactionType: v.transactionType,
      buildingType: v.buildingType,
      ...(v.transactionType === 'خرید' && {
        minBudget: v.buyPriceMin && +v.buyPriceMin,
        maxBudget: v.buyPriceMax && +v.buyPriceMax,
      }),
      ...(v.transactionType === 'رهن کامل' && {
        minBudget: v.mortgagePriceMin && +v.mortgagePriceMin,
        maxBudget: v.mortgagePriceMax && +v.mortgagePriceMax,
      }),
      ...(v.transactionType === 'رهن و اجاره' && {
        minBudget: v.mortgagePriceMin && +v.mortgagePriceMin,
        maxBudget: v.mortgagePriceMax && +v.mortgagePriceMax,
        minRent: v.rentPriceMin && +v.rentPriceMin,
        maxRent: v.rentPriceMax && +v.rentPriceMax,
      }),
      minMeterage: v.meterageMin && +v.meterageMin,
      maxMeterage: v.meterageMax && +v.meterageMax,
      roomCount: v.roomCount,
      facilities: v.facilities,
    };

    const result = removePropertyEmptyRequirement(form);
    mutate(result);
  };

  function validateHandler(values) {
    const errors = {};

    if (!values.type) {
      errors.type = 'وارد نوع مشتری اجباری میباشد !';
    }

    if (!values.fullName) {
      errors.fullName = 'وارد کردن نام و نام خانوادگی اجباری میباشد !';
    }

    const regex = new RegExp(/^09\d{9}$/);

    if (!regex.test(values.phoneNumber)) {
      errors.phoneNumber = 'شماره تلفن وارد شده معتبر نمی باشد !';
    }
    if (values.transactionType == 'خرید' && !values.buyPriceMin && !values.buyPriceMax) {
      errors.buyPriceMin = 'بودجه خرید را وارد کنید';
      errors.buyPriceMax = 'بودجه خرید را وارد کنید';
    }
    if (values.transactionType == 'رهن کامل' && !values.mortgagePriceMin && !values.mortgagePriceMax) {
      errors.mortgagePriceMin = 'بودجه رهن را وارد کنید';
      errors.mortgagePriceMax = 'بودجه رهن را وارد کنید';
    }
    if (values.transactionType == 'رهن و اجاره' && !values.rentPriceMin && !values.rentPriceMax) {
      errors.rentPriceMin = 'بودجه اجاره را وارد کنید';
      errors.rentPriceMax = 'بودجه اجاره را وارد کنید';
    }
    if (values.transactionType == 'رهن و اجاره' && !values.mortgagePriceMin && !values.mortgagePriceMax) {
      errors.mortgagePriceMin = 'بودجه رهن را وارد کنید';
      errors.mortgagePriceMax = 'بودجه رهن را وارد کنید';
    }

    return errors;
  }

  const formikCustomer = useFormik({
    initialValues: {
      type: '',
      fullName: '',
      phoneNumber: '',
      note: '',
      transactionType: '',
      roomCount: '',
      meterageMin: '',
      meterageMax: '',
      buyPriceMin: '',
      buyPriceMax: '',
      mortgagePriceMin: '',
      mortgagePriceMax: '',
      rentPriceMin: '',
      rentPriceMax: '',
      facilities: [],
    },
    validate: validateHandler,
    onSubmit: submitHandler,
  });

  return (
    <SideSheets onOpenChange={setShowModalAdd} title="مشتری جدید">
      <SideSheetsBody count={1}>
        <form
          id="formAddCustomer"
          onSubmit={formikCustomer.handleSubmit}
          className="flex flex-col items-center justify-start gap-8 max-sm:gap-6 text-center w-full pb-16"
        >
          <div className="flex flex-col items-start gap-2 w-full relative">
            <div className="flex flex-row items-center gap-2.5 w-full">
              <p className="text-base text-text-normal dark:text-white">نوع مشتری را مشخص کنید</p>
            </div>
            <div className="grid grid-cols-6 grid-rows-3 gap-2 relative w-full overflow-hidden">
              <div className="col-span-2">
                <input
                  onChange={formikCustomer.handleChange}
                  value="مالک"
                  id="مالک"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="مالک"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  مالک
                </label>
              </div>
              <div className="col-span-2">
                <input
                  onChange={formikCustomer.handleChange}
                  value="خریدار"
                  id="خریدار"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="خریدار"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  خریدار
                </label>
              </div>
              <div className="col-span-2">
                <input
                  onChange={formikCustomer.handleChange}
                  value="مستاجر"
                  id="مستاجر"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="مستاجر"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  مستاجر
                </label>
              </div>
              <div className="col-span-3">
                <input
                  onChange={formikCustomer.handleChange}
                  value="سرمایه گذار"
                  id="سرمایه گذار"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="سرمایه گذار"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  سرمایه گذار
                </label>
              </div>
              <div className="col-span-3">
                <input
                  onChange={formikCustomer.handleChange}
                  value="مشتری ساده"
                  id="مشتری ساده"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="مشتری ساده"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  مشتری ساده
                </label>
              </div>
              <div className="col-span-3">
                <input
                  onChange={formikCustomer.handleChange}
                  value="همکار"
                  id="همکار"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="همکار"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  همکار
                </label>
              </div>
              <div className="col-span-3">
                <input
                  onChange={formikCustomer.handleChange}
                  value="سایر"
                  id="سایر"
                  type="radio"
                  name="type"
                  className={`peer hidden`}
                />
                <label
                  htmlFor="سایر"
                  className={`
                      ${
                        formikCustomer.errors.type && formikCustomer.touched.type
                          ? 'border-error'
                          : 'border-light-grey dark:border-blackFont-700'
                      }         
                      cursor-pointer py-2 px-4 border-[1.5px] text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-md flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                >
                  سایر
                </label>
              </div>
            </div>
            {formikCustomer.errors.type && formikCustomer.touched.type && (
              <p className="text-xs text-error absolute -bottom-5">{formikCustomer.errors.type}</p>
            )}
          </div>
          <div className="flex items-center gap-2 relative w-[310px]">
            <p className="text-base text-text-normal dark:text-white">میزان اهمیت مشتری</p>
            <div>
              <Select
                onChange={(e) => {
                  setPriority(e.target.value);
                }}
                disallowEmptySelection={true}
                value={priority}
                defaultSelectedKeys={[priority]}
                showScrollIndicators={true}
                style={{
                  height: '24px',
                  backgroundColor:
                    isDark && priority == 'بدون وضعیت' ? priorityStyle[priority].darkBg : priorityStyle[priority].bg,
                  color:
                    isDark && priority == 'بدون وضعیت'
                      ? priorityStyle[priority].darkColor
                      : priorityStyle[priority].color,
                  boxShadow: 'none',
                  fontSize: '16px !important',
                  borderRadius: '50px',
                  border: priority == 'بدون وضعیت' ? (isDark ? '1.5px dashed #9CA3AF' : '1.5px dashed #61616199') : '',
                }}
                classNames={{
                  trigger: clsx(priority == 'بدون وضعیت' && 'dark:bg-blackFont-700 dark:border-blackFont-600'),
                  selectorIcon: clsx(
                    `rounded-full`,
                    priority == 'بالقوه' && 'bg-success stroke-white ',
                    priority == 'میانه' && 'bg-warning stroke-white ',
                    priority == 'عادی' && 'bg-error stroke-white ',
                    priority == 'بدون وضعیت' &&
                      'border-1 border-dashed border-[#61616199] dark:border-white/50 stroke-[#61616199] dark:stroke-white/70',
                  ),
                  base: 'min-w-fit whitespace-nowrap',
                  value:
                    `group-data-[has-value=true]:text-[${priorityStyle[priority].color}] text-[${priorityStyle[priority].color}] ` +
                    (priority == 'بدون وضعیت'
                      ? 'dark:group-data-[has-value=true]:text-[#9CA3AF] dark:text-[#9CA3AF] '
                      : '') +
                    'text-sm font-light whitespace-nowrap min-w-fit ml-5 group-data-[has-value=true]:font-normal',
                  popoverContent:
                    'p-0 rounded-lg border-none border-light-grey dark:border-blackFont-700 dark:bg-blackFont-800 w-[160px]',
                  listbox: 'p-0 dark:bg-blackFont-800',
                  listboxWrapper: 'p-1',
                }}
                className="custom-select"
                listboxProps={{
                  style: {
                    maxHeight: 'max-content',
                  },
                  itemClasses: {
                    base: [
                      'data-[hover=true]:bg-gray',
                      'data-[selectable=true]:focus:bg-gray',
                      'data-[selected=true]:text-primary',
                      'data-[selected=true]:focus:text-primary',
                      'data-[pressed=true]:opacity-100',
                      'data-[focus=true]:bg-secondary',
                      'dark:data-[hover=true]:bg-blackFont-700',
                      'dark:data-[selectable=true]:focus:bg-blackFont-700',
                    ],
                  },
                }}
                placeholder="انتخاب کنید"
              >
                <SelectItem key="بالقوه" textValue="بالقوه">
                  <p className="text-[12px] text-success flex gap-1 items-center">
                    <span className="bg-success w-4 h-4 rounded-full relative">
                      <span className="bg-success w-3 h-3 border-2 border-white rounded-full absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"></span>
                    </span>
                    بالقوه
                  </p>
                </SelectItem>
                <SelectItem key="میانه" textValue="میانه">
                  <p className="text-[12px] text-warning flex gap-1 items-center">
                    <span className="bg-warning w-4 h-4 rounded-full relative">
                      <span className="bg-warning w-3 h-3 border-2 border-white rounded-full absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"></span>
                    </span>
                    میانه
                  </p>
                </SelectItem>
                <SelectItem key="عادی" textValue="عادی">
                  <p className="text-[12px] text-error flex gap-1 items-center">
                    <span className="bg-error w-4 h-4 rounded-full relative">
                      <span className="bg-error w-3 h-3 border-2 border-white rounded-full absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"></span>
                    </span>
                    عادی
                  </p>
                </SelectItem>
                <SelectItem key="بدون وضعیت" textValue="بدون وضعیت">
                  <p className="text-[12px] text-text-normal dark:text-white flex gap-1 items-center">
                    <span className="bg-text-normal dark:bg-white w-4 h-4 rounded-full relative">
                      <span className="bg-text-normal dark:bg-white w-3 h-3 border-2 border-white dark:border-blackFont-800 rounded-full absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2"></span>
                    </span>
                    بدون وضعیت
                  </p>
                </SelectItem>
              </Select>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 w-full relative">
            <div className="flex flex-row items-center gap-1.5">
              <p className="text-sm text-text-normal dark:text-white">نام و نام خانوادگی</p>
              <p className="text-error text-xs font-medium">اجباری</p>
            </div>
            <div className="flex flex-row flex-wrap gap-2 w-full">
              <input
                placeholder="برای مثال جواد عزتی"
                onChange={formikCustomer.handleChange}
                value={formikCustomer.values.fullName}
                name="fullName"
                type="text"
                className={`
                  ${
                    formikCustomer.errors.fullName && formikCustomer.touched.fullName
                      ? 'border-error'
                      : 'border-light-grey dark:border-blackFont-700'
                  }
                  disabled:bg-light-grey/50 dark:disabled:bg-blackFont-700/50 border-[1px] h-[42px] rounded-lg py-3.5 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-sm transition-all bg-white dark:bg-blackFont-800`}
              />
            </div>
            {formikCustomer.errors.fullName && formikCustomer.touched.fullName && (
              <p className="text-xs text-error absolute -bottom-5">{formikCustomer.errors.fullName}</p>
            )}
          </div>
          <div className="flex flex-col items-start gap-2 w-full relative">
            <div className="flex flex-row items-center gap-1.5">
              <p className="text-sm text-text-normal dark:text-white">شماره تماس</p>
              <p className="text-error text-xs font-medium">اجباری</p>
            </div>
            <div className="flex flex-row items-center gap-2 w-full">
              <input
                placeholder="برای مثال 09125858199"
                onChange={formikCustomer.handleChange}
                value={formikCustomer.values.phoneNumber}
                name="phoneNumber"
                type="tel"
                onInput={patternTelInput}
                className={`
                  ${
                    formikCustomer.errors.phoneNumber && formikCustomer.touched.phoneNumber
                      ? 'border-error'
                      : 'border-light-grey dark:border-blackFont-700'
                  }
                  disabled:bg-light-grey/50 dark:disabled:bg-blackFont-700/50 border-[1px] h-[42px] rounded-lg py-3.5 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-sm transition-all bg-white dark:bg-blackFont-800`}
              />
            </div>
            {formikCustomer.errors.phoneNumber && formikCustomer.touched.phoneNumber && (
              <p className="text-xs text-error absolute -bottom-5">{formikCustomer.errors.phoneNumber}</p>
            )}
          </div>
          <div className="flex flex-col items-start gap-2 w-full">
            <div className="flex flex-row items-center gap-1.5">
              <p className="text-sm text-text-normal dark:text-white">یادداشت و توضیحات</p>
            </div>
            <div className="flex flex-row flex-wrap gap-2 w-full">
              <textarea
                placeholder="درصورت تمایل میتوانید اطلاعات کامل تری از مشتری را وارد نمایید"
                onChange={formikCustomer.handleChange}
                value={formikCustomer.values.note}
                name="note"
                rows={6}
                type="text"
                className={`
                  disabled:bg-light-grey/50 dark:disabled:bg-blackFont-700/50 border-light-grey dark:border-blackFont-700 border-[1px] resize-none focus:outline-0 focus:ring-0 rounded-lg py-3.5 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-sm transition-all bg-white dark:bg-blackFont-800`}
              />
            </div>
          </div>
          {!showRequirement ? (
            <div className="w-full flex flex-col items-center justify-center gap-1">
              <div
                onClick={() => setShowRequirement(true)}
                className="flex items-center gap-1 cursor-pointer border-2 border-primary text-primary font-medium w-full rounded-lg text-xs py-1.5 justify-center dark:bg-blackFont-800"
              >
                <svg className="w-[18px] h-[18px]">
                  <use href="#plus"></use>
                </svg>
                افزودن نیازمندی برای مشتری
              </div>
              <p className="text-sm text-text-normal dark:text-white">درج اطلاعات تکمیلی نیاز مشتری</p>
            </div>
          ) : (
            <div className="pt-5 gap-5 border-t-1 border-t-light-grey dark:border-t-blackFont-700 w-full flex flex-col items-start overflow-hidden transition-all">
              <p className="text-text-hard dark:text-white font-semibold text-sm">اطلاعات نیازمندی</p>
              <div className="flex flex-col items-start gap-2 w-full relative">
                <p className="text-base text-text-normal dark:text-white flex flex-row items-center gap-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-blackFont-600 dark:text-white"
                  >
                    <path
                      d="M6.6665 5.00008V4.16675C6.6665 2.78604 7.78579 1.66675 9.1665 1.66675H10.8332C12.2139 1.66675 13.3332 2.78604 13.3332 4.16675V5.00008M1.6665 8.62298C1.6665 8.62298 4.26487 10.3538 8.31456 10.7591M18.3332 8.62298C18.3332 8.62298 15.7348 10.3538 11.6851 10.7591M4.99984 18.3334H14.9998C16.8408 18.3334 18.3332 16.841 18.3332 15.0001V8.33342C18.3332 6.49247 16.8408 5.00008 14.9998 5.00008H4.99984C3.15889 5.00008 1.6665 6.49247 1.6665 8.33342V15.0001C1.6665 16.841 3.15889 18.3334 4.99984 18.3334Z"
                      stroke="currentColor"
                      strokeLinecap="round"
                    />
                    <path
                      d="M11.6668 10.1334V10.9667C11.6668 10.975 11.6668 10.975 11.6668 10.9834C11.6668 11.8917 11.6585 12.6334 10.0002 12.6334C8.35016 12.6334 8.3335 11.9 8.3335 10.9917V10.1334C8.3335 9.30005 8.3335 9.30005 9.16683 9.30005H10.8335C11.6668 9.30005 11.6668 9.30005 11.6668 10.1334Z"
                      stroke="currentColor"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  نوع معامله :
                </p>
                <div className="grid grid-cols-6 grid-rows-2 gap-x-1.5 gap-y-2.5 relative w-full overflow-hidden">
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="خرید"
                      id="خرید"
                      type="radio"
                      name="transactionType"
                      className={`peer hidden`}
                      checked={'خرید' == formikCustomer.values.transactionType}
                    />
                    <label
                      htmlFor="خرید"
                      className={`cursor-pointer border-light-grey dark:border-blackFont-700 py-2 px-4 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      خرید
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="رهن و اجاره"
                      id="رهن و اجاره"
                      type="radio"
                      name="transactionType"
                      className={`peer hidden`}
                      checked={'رهن و اجاره' == formikCustomer.values.transactionType}
                    />
                    <label
                      htmlFor="رهن و اجاره"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      رهن و اجاره
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="رهن کامل"
                      id="رهن کامل"
                      type="radio"
                      name="transactionType"
                      className={`peer hidden`}
                      checked={'رهن کامل' == formikCustomer.values.transactionType}
                    />
                    <label
                      htmlFor="رهن کامل"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      رهن کامل
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="پیش خرید"
                      id="پیش خرید"
                      type="radio"
                      name="transactionType"
                      className={`peer hidden`}
                      checked={'پیش خرید' == formikCustomer.values.transactionType}
                    />
                    <label
                      htmlFor="پیش خرید"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      پیش خرید
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="مشارکتی"
                      id="مشارکتی"
                      type="radio"
                      name="transactionType"
                      className={`peer hidden`}
                      checked={'مشارکتی' == formikCustomer.values.transactionType}
                    />
                    <label
                      htmlFor="مشارکتی"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      مشارکتی
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start gap-2 w-full relative">
                <p className="text-base text-text-normal dark:text-white flex flex-row items-center gap-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-blackFont-600 dark:text-white"
                  >
                    <path
                      d="M1.66748 18.333H18.3341"
                      stroke="currentColor"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.45898 18.3331L2.50065 8.30812C2.50065 7.79979 2.74232 7.3165 3.14232 6.99983L8.97565 2.45815C9.57565 1.99149 10.4173 1.99149 11.0257 2.45815L16.859 6.99149C17.2673 7.30816 17.5006 7.79145 17.5006 8.30812V18.3331"
                      stroke="currentColor"
                      strokeMiterlimit="10"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12.9173 9.1665H7.08398C6.39232 9.1665 5.83398 9.72484 5.83398 10.4165V18.3332H14.1673V10.4165C14.1673 9.72484 13.609 9.1665 12.9173 9.1665Z"
                      stroke="currentColor"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.33447 13.5415V14.7915"
                      stroke="currentColor"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.75098 6.25H11.251"
                      stroke="currentColor"
                      strokeMiterlimit="10"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  نوع ملک :
                </p>
                <div className="grid grid-cols-6 grid-rows-2 gap-x-1.5 gap-y-2.5 relative w-full overflow-hidden">
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="آپارتمان"
                      id="آپارتمان"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'آپارتمان' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="آپارتمان"
                      className={`cursor-pointer border-light-grey dark:border-blackFont-700 py-2 px-4 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      آپارتمان
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="خانه ویلایی"
                      id="خانه ویلایی"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'خانه ویلایی' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="خانه ویلایی"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      خانه ویلایی
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="سوییت"
                      id="سوییت"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'سوییت' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="سوییت"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      سوییت
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="ویلا"
                      id="ویلا"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'ویلا' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="ویلا"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      ویلا
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="زمین"
                      id="زمین"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'زمین' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="زمین"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      زمین
                    </label>
                  </div>
                  <div className="col-span-2">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="باغ"
                      id="باغ"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'باغ' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="باغ"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      باغ
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="مغازه و تجاری"
                      id="مغازه و تجاری"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'مغازه و تجاری' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="مغازه و تجاری"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      مغازه و تجاری
                    </label>
                  </div>
                  <div className="col-span-3">
                    <input
                      onChange={formikCustomer.handleChange}
                      value="دفتر کار اداری"
                      id="دفتر کار اداری"
                      type="radio"
                      name="buildingType"
                      className={`peer hidden`}
                      checked={'دفتر کار اداری' == formikCustomer.values.buildingType}
                    />
                    <label
                      htmlFor="دفتر کار اداری"
                      className={`cursor-pointer py-2 px-4 border-light-grey dark:border-blackFont-700 border-1 text-sm max-sm:text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 peer-checked:dark:bg-primary/20 rounded-lg flex items-center justify-center transition-all bg-white dark:bg-blackFont-800`}
                    >
                      دفتر کار اداری
                    </label>
                  </div>
                </div>
              </div>
              {formikCustomer.values.transactionType == 'خرید' && (
                <div className="flex flex-col items-start gap-2 w-full">
                  <p className="text-base text-text-normal dark:text-white flex items-center gap-1">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-blackFont-600 dark:text-white"
                    >
                      <path
                        d="M11.6668 10.6251C11.6668 9.81967 10.9206 9.16675 10.0002 9.16675C9.07969 9.16675 8.3335 9.81967 8.3335 10.6251C8.3335 11.4305 9.07969 12.0834 10.0002 12.0834C10.9206 12.0834 11.6668 12.7363 11.6668 13.5417C11.6668 14.3472 10.9206 15.0001 10.0002 15.0001C9.07969 15.0001 8.3335 14.3472 8.3335 13.5417"
                        stroke="currentColor"
                        strokeLinecap="round"
                      />
                      <path
                        d="M10 7.91675V9.16675"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path d="M10 15V16.25" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                      <path
                        d="M4.51555 9.7861C5.30242 7.4255 7.51155 5.83325 9.99984 5.83325V5.83325C12.4881 5.83325 14.6973 7.4255 15.4841 9.7861L15.7007 10.4359C16.9958 14.3211 14.0952 18.3333 9.99984 18.3333V18.3333C5.90449 18.3333 3.0039 14.3211 4.29896 10.4359L4.51555 9.7861Z"
                        stroke="currentColor"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.7381 5.83341L8.2616 5.83341L7.09403 4.49421C5.9495 3.18144 7.28471 1.21923 8.95573 1.75829L9.72887 2.0077C9.9049 2.06448 10.0948 2.06448 10.2708 2.0077L11.0439 1.75829C12.715 1.21923 14.0502 3.18144 12.9056 4.49421L11.7381 5.83341Z"
                        stroke="currentColor"
                        strokeLinejoin="round"
                      />
                    </svg>
                    بودجه خرید :
                  </p>
                  <div className="flex flex-col gap-2.5 w-full relative text-text-normal dark:text-white">
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>از</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.buyPriceMin)}
                        name="buyPriceMin"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.buyPriceMin && formikCustomer.touched.buyPriceMin
                            ? 'border-error'
                            : 'border-light-grey dark:border-blackFont-700'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800`}
                        placeholder="حداقل بودجه خرید"
                      />
                    </div>
                    {formikCustomer.values.buyPriceMin && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.buyPriceMin))} تومان
                      </p>
                    )}
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>تا</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.buyPriceMax)}
                        name="buyPriceMax"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.buyPriceMin && formikCustomer.touched.buyPriceMin
                            ? 'border-error'
                            : 'border-light-grey dark:border-blackFont-700'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800`}
                        placeholder="حداکثر بودجه خرید"
                      />
                    </div>
                    {formikCustomer.values.buyPriceMax && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.buyPriceMax))} تومان
                      </p>
                    )}
                  </div>
                </div>
              )}
              {formikCustomer.values.transactionType == 'رهن کامل' && (
                <div className="flex flex-col items-start gap-2 w-full">
                  <p className="text-base text-text-normal flex items-center gap-1">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.6668 10.6251C11.6668 9.81967 10.9206 9.16675 10.0002 9.16675C9.07969 9.16675 8.3335 9.81967 8.3335 10.6251C8.3335 11.4305 9.07969 12.0834 10.0002 12.0834C10.9206 12.0834 11.6668 12.7363 11.6668 13.5417C11.6668 14.3472 10.9206 15.0001 10.0002 15.0001C9.07969 15.0001 8.3335 14.3472 8.3335 13.5417"
                        stroke="#616161"
                        strokeLinecap="round"
                      />
                      <path d="M10 7.91675V9.16675" stroke="#616161" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 15V16.25" stroke="#616161" strokeLinecap="round" strokeLinejoin="round" />
                      <path
                        d="M4.51555 9.7861C5.30242 7.4255 7.51155 5.83325 9.99984 5.83325V5.83325C12.4881 5.83325 14.6973 7.4255 15.4841 9.7861L15.7007 10.4359C16.9958 14.3211 14.0952 18.3333 9.99984 18.3333V18.3333C5.90449 18.3333 3.0039 14.3211 4.29896 10.4359L4.51555 9.7861Z"
                        stroke="#616161"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.7381 5.83341L8.2616 5.83341L7.09403 4.49421C5.9495 3.18144 7.28471 1.21923 8.95573 1.75829L9.72887 2.0077C9.9049 2.06448 10.0948 2.06448 10.2708 2.0077L11.0439 1.75829C12.715 1.21923 14.0502 3.18144 12.9056 4.49421L11.7381 5.83341Z"
                        stroke="#616161"
                        strokeLinejoin="round"
                      />
                    </svg>
                    بودجه رهن کامل :
                  </p>
                  <div className="flex flex-col gap-2.5 w-full relative text-text-normal">
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>از</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.mortgagePriceMin)}
                        name="mortgagePriceMin"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.mortgagePriceMin && formikCustomer.touched.mortgagePriceMin
                            ? 'border-error'
                            : 'border-light-grey dark:border-blackFont-700'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800`}
                        placeholder="حداقل بودجه رهن کامل"
                      />
                    </div>
                    {formikCustomer.values.mortgagePriceMin && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.mortgagePriceMin))} تومان
                      </p>
                    )}
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>تا</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.mortgagePriceMax)}
                        name="mortgagePriceMax"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.mortgagePriceMax && formikCustomer.touched.mortgagePriceMax
                            ? 'border-error'
                            : 'border-light-grey dark:border-blackFont-700'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800`}
                        placeholder="حداکثر بودجه رهن کامل"
                      />
                    </div>
                    {formikCustomer.values.mortgagePriceMax && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.mortgagePriceMax))} تومان
                      </p>
                    )}
                  </div>
                </div>
              )}
              {formikCustomer.values.transactionType == 'رهن و اجاره' && (
                <div className="flex flex-col items-start gap-2 w-full">
                  <p className="text-base text-text-normal flex items-center gap-1">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.6668 10.6251C11.6668 9.81967 10.9206 9.16675 10.0002 9.16675C9.07969 9.16675 8.3335 9.81967 8.3335 10.6251C8.3335 11.4305 9.07969 12.0834 10.0002 12.0834C10.9206 12.0834 11.6668 12.7363 11.6668 13.5417C11.6668 14.3472 10.9206 15.0001 10.0002 15.0001C9.07969 15.0001 8.3335 14.3472 8.3335 13.5417"
                        stroke="#616161"
                        strokeLinecap="round"
                      />
                      <path d="M10 7.91675V9.16675" stroke="#616161" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 15V16.25" stroke="#616161" strokeLinecap="round" strokeLinejoin="round" />
                      <path
                        d="M4.51555 9.7861C5.30242 7.4255 7.51155 5.83325 9.99984 5.83325V5.83325C12.4881 5.83325 14.6973 7.4255 15.4841 9.7861L15.7007 10.4359C16.9958 14.3211 14.0952 18.3333 9.99984 18.3333V18.3333C5.90449 18.3333 3.0039 14.3211 4.29896 10.4359L4.51555 9.7861Z"
                        stroke="#616161"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.7381 5.83341L8.2616 5.83341L7.09403 4.49421C5.9495 3.18144 7.28471 1.21923 8.95573 1.75829L9.72887 2.0077C9.9049 2.06448 10.0948 2.06448 10.2708 2.0077L11.0439 1.75829C12.715 1.21923 14.0502 3.18144 12.9056 4.49421L11.7381 5.83341Z"
                        stroke="#616161"
                        strokeLinejoin="round"
                      />
                    </svg>
                    بودجه رهن :
                  </p>
                  <div className="flex flex-col gap-2.5 w-full relative text-text-normal">
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>از</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.mortgagePriceMin)}
                        name="mortgagePriceMin"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.mortgagePriceMin && formikCustomer.touched.mortgagePriceMin
                            ? 'border-error'
                            : 'border-light-grey'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 placeholder:font-light text-text-hard text-base transition-all`}
                        placeholder="حداقل بودجه رهن"
                      />
                    </div>
                    {formikCustomer.values.mortgagePriceMin && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.mortgagePriceMin))} تومان
                      </p>
                    )}
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>تا</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.mortgagePriceMax)}
                        name="mortgagePriceMax"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.mortgagePriceMax && formikCustomer.touched.mortgagePriceMax
                            ? 'border-error'
                            : 'border-light-grey'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 placeholder:font-light text-text-hard text-base transition-all`}
                        placeholder="حداکثر بودجه رهن"
                      />
                    </div>
                    {formikCustomer.values.mortgagePriceMax && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.mortgagePriceMax))} تومان
                      </p>
                    )}
                  </div>
                </div>
              )}
              {formikCustomer.values.transactionType == 'رهن و اجاره' && (
                <div className="flex flex-col items-start gap-2 w-full">
                  <p className="text-base text-text-normal flex items-center gap-1">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.6668 10.6251C11.6668 9.81967 10.9206 9.16675 10.0002 9.16675C9.07969 9.16675 8.3335 9.81967 8.3335 10.6251C8.3335 11.4305 9.07969 12.0834 10.0002 12.0834C10.9206 12.0834 11.6668 12.7363 11.6668 13.5417C11.6668 14.3472 10.9206 15.0001 10.0002 15.0001C9.07969 15.0001 8.3335 14.3472 8.3335 13.5417"
                        stroke="#616161"
                        strokeLinecap="round"
                      />
                      <path d="M10 7.91675V9.16675" stroke="#616161" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 15V16.25" stroke="#616161" strokeLinecap="round" strokeLinejoin="round" />
                      <path
                        d="M4.51555 9.7861C5.30242 7.4255 7.51155 5.83325 9.99984 5.83325V5.83325C12.4881 5.83325 14.6973 7.4255 15.4841 9.7861L15.7007 10.4359C16.9958 14.3211 14.0952 18.3333 9.99984 18.3333V18.3333C5.90449 18.3333 3.0039 14.3211 4.29896 10.4359L4.51555 9.7861Z"
                        stroke="#616161"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M11.7381 5.83341L8.2616 5.83341L7.09403 4.49421C5.9495 3.18144 7.28471 1.21923 8.95573 1.75829L9.72887 2.0077C9.9049 2.06448 10.0948 2.06448 10.2708 2.0077L11.0439 1.75829C12.715 1.21923 14.0502 3.18144 12.9056 4.49421L11.7381 5.83341Z"
                        stroke="#616161"
                        strokeLinejoin="round"
                      />
                    </svg>
                    بودجه اجاره :
                  </p>
                  <div className="flex flex-col gap-2.5 w-full relative text-text-normal">
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>از</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.rentPriceMin)}
                        name="rentPriceMin"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.rentPriceMin && formikCustomer.touched.rentPriceMin
                            ? 'border-error'
                            : 'border-light-grey dark:border-blackFont-700'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800`}
                        placeholder="حداقل بودجه اجاره"
                      />
                    </div>
                    {formikCustomer.values.rentPriceMin && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.rentPriceMin))} تومان
                      </p>
                    )}
                    <div className="flex flex-row gap-2.5 items-center w-full">
                      <p>تا</p>
                      <input
                        onChange={formikCustomer.handleChange}
                        value={convertNumberToPrice(formikCustomer.values.rentPriceMax)}
                        name="rentPriceMax"
                        type="tel"
                        onInput={patternTelInput}
                        className={` ${
                          formikCustomer.errors.rentPriceMax && formikCustomer.touched.rentPriceMax
                            ? 'border-error'
                            : 'border-light-grey dark:border-blackFont-700'
                        } text-center border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800`}
                        placeholder="حداکثر بودجه اجاره"
                      />
                    </div>
                    {formikCustomer.values.rentPriceMax && (
                      <p className="font-bold text-sm text-primary text-right pr-5">
                        {Num2persian(Number(formikCustomer.values.rentPriceMax))} تومان
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col items-start gap-2 w-full">
                <p className="text-base text-text-normal dark:text-white flex items-center gap-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-blackFont-600 dark:text-white"
                  >
                    <path
                      d="M5.5 2.5H13.5C15.7091 2.5 17.5 4.29086 17.5 6.5V14.5C17.5 16.7091 15.7091 18.5 13.5 18.5H5.5C3.29086 18.5 1.5 16.7091 1.5 14.5V6.5C1.5 4.29086 3.29086 2.5 5.5 2.5Z"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10.5 6.5H13.5M13.5 6.5V9.5M13.5 6.5L5.5 14.5M5.5 14.5V11.5M5.5 14.5H8.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  متراژ :
                </p>
                <div className="flex flex-col gap-2.5 w-full relative text-text-normal dark:text-white">
                  <div className="flex flex-row gap-2.5 items-center w-full">
                    <p>از</p>
                    <input
                      onChange={formikCustomer.handleChange}
                      value={formikCustomer.values.meterageMin}
                      name="meterageMin"
                      type="tel"
                      onInput={patternTelInput}
                      className="text-center border-light-grey dark:border-blackFont-700 border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800"
                      placeholder="حداقل متراژ (متر)"
                    />
                  </div>
                  <div className="flex flex-row gap-2.5 items-center w-full">
                    <p>تا</p>
                    <input
                      onChange={formikCustomer.handleChange}
                      value={formikCustomer.values.meterageMax}
                      name="meterageMax"
                      type="tel"
                      onInput={patternTelInput}
                      className="text-center border-light-grey dark:border-blackFont-700 border-1 rounded-lg py-2 px-4 w-full placeholder:text-text-normal/70 dark:placeholder:text-blackFont-400 placeholder:font-light text-text-hard dark:text-white text-base transition-all bg-white dark:bg-blackFont-800"
                      placeholder="حداکثر متراژ (متر)"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start gap-2.5 w-full relative">
                <div className="flex flex-row items-center gap-1">
                  <p className="text-text-normal dark:text-white text-base font-semibold">تعداد اتاق</p>
                </div>
                <div className="flex flex-row items-center h-[42px] w-full">
                  <div
                    onClick={() => {
                      formikCustomer.setFieldValue('roomCount', Number(formikCustomer.values.roomCount) + 1);
                    }}
                    className="cursor-pointer h-full py-4 px-5 border-1 border-light-grey dark:border-blackFont-700 rounded-r-lg text-2xl flex items-center justify-center bg-white dark:bg-blackFont-800"
                  >
                    <svg
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-blackFont-800 dark:text-white"
                    >
                      <path d="M8.5 1V15" stroke="currentColor" strokeLinecap="round" />
                      <path d="M1 7.5L16 7.5" stroke="currentColor" strokeLinecap="round" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    name="roomCount"
                    placeholder="0"
                    value={formikCustomer.values.roomCount}
                    onChange={formikCustomer.handleChange}
                    onInput={patternTelInput}
                    className="h-full text-center text-base placeholder:text-text-normal dark:placeholder:text-blackFont-400 text-text-hard dark:text-white border-y-1 border-y-light-grey dark:border-y-blackFont-700 w-full bg-white dark:bg-blackFont-800"
                  />
                  <div
                    onClick={() => {
                      if (formikCustomer.values.roomCount > 0) {
                        formikCustomer.setFieldValue('roomCount', Number(formikCustomer.values.roomCount) - 1);
                      }
                    }}
                    className="cursor-pointer h-full py-4 px-5 border-1 border-light-grey dark:border-blackFont-700 rounded-l-lg text-2xl flex items-center justify-center bg-white dark:bg-blackFont-800"
                  >
                    <svg
                      width="17"
                      height="2"
                      viewBox="0 0 17 2"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-blackFont-800 dark:text-white"
                    >
                      <path d="M1 1L16 1" stroke="currentColor" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start gap-2 w-full relative">
                <p className="text-base text-text-normal dark:text-white flex flex-row items-center gap-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-blackFont-600 dark:text-white"
                  >
                    <path
                      d="M11.7718 2.5H14.1262C14.6323 2.5 15.1143 2.71917 15.4505 3.10223L17.8856 5.87624C18.4507 6.52001 18.4843 7.47958 17.9657 8.1623L11.4043 16.7997C10.695 17.7334 9.30466 17.7334 8.59536 16.7997L2.03396 8.1623C1.51534 7.47958 1.54897 6.52001 2.11408 5.87624L4.54916 3.10223C4.88541 2.71917 5.36739 2.5 5.87352 2.5H8.42479M11.7718 2.5L13.5437 6.98571M11.7718 2.5H8.42479M13.5437 6.98571H17.9735M13.5437 6.98571L9.99984 17.3028L6.45597 6.98571M13.5437 6.98571H6.45597M8.42479 2.5L6.45597 6.98571M2.02614 6.98571H6.45597"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  امکانات :
                </p>
                <div
                  className={`grid grid-cols-2 gap-x-1.5 gap-y-2.5 relative w-full overflow-hidden transition-all ${
                    showMore ? 'max-h-[1100px]' : 'max-h-[295px]'
                  }`}
                >
                  {facilitiesData.map((i) => (
                    <div key={i.name} className="col-span-1">
                      <input
                        onChange={formikCustomer.handleChange}
                        value={i.name}
                        id={i.name}
                        type="checkbox"
                        name="facilities"
                        className={`peer hidden`}
                        checked={formikCustomer.values.facilities.includes(i.name)}
                      />
                      <label
                        htmlFor={i.name}
                        className={`cursor-pointer h-10 flex items-center gap-1 border-light-grey dark:border-blackFont-700 py-1 px-2 border-1 text-xs text-text-normal dark:text-white whitespace-nowrap peer-checked:text-primary peer-checked:border-primary peer-checked:bg-lighter-primary-10 rounded-lg justify-center transition-all bg-white dark:bg-blackFont-800`}
                      >
                        {i.icon}
                        {i.name}
                      </label>
                    </div>
                  ))}
                </div>
                <p
                  onClick={() => setShowMore(!showMore)}
                  className="mx-auto text-primary dark:text-primary text-sm cursor-pointer"
                >
                  {showMore ? 'بستن' : 'مشاهده همه'}
                </p>
              </div>
            </div>
          )}
        </form>
      </SideSheetsBody>
      <SideSheetsFooter>
        <Button
          form="formAddCustomer"
          type="submit"
          isLoading={isPending}
          height="h-10"
          width="w-full"
          variant="primary"
          font="sm"
        >
          افزودن مشتری
        </Button>
      </SideSheetsFooter>
    </SideSheets>
  );
}
